import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import SmsCustomerDialog from "@/components/SmsCustomerDialog";
import {
  CheckCircle2, ClipboardList, CloudSun, Loader2, MapPin, MessageSquarePlus, Navigation, PackageCheck,
  Radar, RefreshCw, RotateCcw, Smartphone, Truck,
} from "lucide-react";

const STEPS = [
  { label: "Booked", icon: PackageCheck },
  { label: "In Transit", icon: Truck },
  { label: "Out for Delivery", icon: Navigation },
  { label: "Delivered", icon: CheckCircle2 },
];

function Fact({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="label-caps text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-semibold">{value}</div>
    </div>
  );
}

export default function ShipmentStatusCard({ quote, onReset }) {
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [trackingUrl, setTrackingUrl] = useState("");
  const [smsOpen, setSmsOpen] = useState(false);
  const [repEnabled, setRepEnabled] = useState(
    (quote.track_subscribers || []).some((s) => s.rep_user_id === user?.id)
  );
  const [repBusy, setRepBusy] = useState(false);
  const [phonePromptOpen, setPhonePromptOpen] = useState(false);
  const [phoneVal, setPhoneVal] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: link } = await api.get(`/quotes/${quote.id}/tracking-link`);
      setTrackingUrl(link.url || `${window.location.origin}/track/${link.token}`);
      const { data: trk } = await api.get(`/public/track/${link.token}`);
      setData(trk);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [quote.id]);
  useEffect(() => { load(); }, [load]);

  const resetTracking = async () => {
    if (!window.confirm(
      "Reset this shipment's tracking?\n\nThis clears any wrong \"delivered\"/\"picked up\" state (e.g. a load marked delivered before it shipped) and re-pulls fresh tracking from the carrier, so the real pickup/delivery notifications can fire when they actually happen."
    )) return;
    setResetBusy(true);
    try {
      const { data: res } = await api.post(`/shipments/${quote.id}/reset-tracking`);
      toast.success(`Tracking reset — now: ${res.status || "re-checking"}`);
      await load();
      onReset?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResetBusy(false);
    }
  };

  const toggleRep = async (phone = "") => {
    setRepBusy(true);
    try {
      const { data: res } = await api.post(`/quotes/${quote.id}/rep-sms`, { enable: !repEnabled, phone });
      setRepEnabled(!!res.enabled);
      setPhonePromptOpen(false);
      toast.success(res.enabled ? `You'll get texts for this shipment at ${res.phone}` : "Text updates turned off");
    } catch (e) {
      const msg = apiError(e);
      if (!repEnabled && /mobile number/i.test(msg)) {
        setPhonePromptOpen(true);
      } else {
        toast.error(msg);
      }
    } finally {
      setRepBusy(false);
    }
  };

  const stage = data?.stage ?? 0;
  const delivered = !!data?.delivered;

  return (
    <div className="border border-border bg-card" data-testid="shipment-status-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Radar className="h-4 w-4 text-[hsl(var(--primary))]" />
          <span className="font-heading text-base font-bold">Live Shipment Status</span>
        </div>
        <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="shipment-status-refresh" className="h-7 gap-1 px-2 text-xs">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        {isAdmin && (
          <Button variant="ghost" size="sm" onClick={resetTracking} disabled={resetBusy || loading}
            data-testid="shipment-reset-tracking" className="h-7 gap-1 px-2 text-xs text-muted-foreground"
            title="Clear a wrong delivered/shipped state and re-pull fresh carrier tracking">
            <RotateCcw className={`h-3.5 w-3.5 ${resetBusy ? "animate-spin" : ""}`} /> Reset
          </Button>
        )}
        </div>
      </div>
      <div className="space-y-4 p-4">
        {error && <div className="text-sm text-muted-foreground">{error}</div>}
        {!data && !error && <div className="py-6 text-center text-sm text-muted-foreground">Loading shipment status…</div>}
        {data && (
          <>
            <div>
              <div className={`font-heading text-xl font-bold ${delivered ? "text-[hsl(var(--success))]" : ""}`} data-testid="shipment-status-text">
                {delivered && <CheckCircle2 className="mr-1.5 inline h-5 w-5 -translate-y-0.5" />}{data.status}
              </div>
              {data.current_location && (
                <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" /> Currently near {data.current_location}
                </div>
              )}
            </div>
            <div>
              <div className="relative mx-1 h-1 rounded-full bg-secondary">
                <div className="absolute inset-y-0 left-0 rounded-full bg-[hsl(var(--success))] transition-all duration-700"
                  style={{ width: `${(stage / (STEPS.length - 1)) * 100}%` }} />
              </div>
              <div className="mt-2 grid grid-cols-4 gap-1">
                {STEPS.map((st, i) => {
                  const Icon = st.icon;
                  const active = i <= stage;
                  return (
                    <div key={st.label} className="flex flex-col items-center gap-1 text-center">
                      <div className={`flex h-7 w-7 items-center justify-center rounded-full border ${
                        active ? "border-[hsl(var(--success))]/60 bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"
                               : "border-border bg-secondary/40 text-muted-foreground/50"}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <span className={`text-[10px] font-semibold ${active ? "text-foreground" : "text-muted-foreground/60"}`}>{st.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-5 gap-y-2 border-t border-border pt-3 sm:grid-cols-4">
              <Fact label="Carrier" value={data.carrier} />
              <Fact label={data.bol_type === "GlobalTranz" ? "BOL #" : "PRO #"} value={data.bol_number} />
              <Fact label="Pickup" value={(data.pickup_date || "").slice(0, 10)} />
              {delivered
                ? <Fact label="Delivered" value={(data.delivery_date || "").slice(0, 10) || "Confirmed"} />
                : <Fact label="Est. Delivery" value={(data.estimated_delivery || "").slice(0, 10)} />}
            </div>
            {!delivered && data.weather && (data.weather.destination || data.weather.origin) && (() => {
              const w = data.weather;
              const worst = [w.origin, w.destination].filter(Boolean).sort((a, b) => (b.score || 0) - (a.score || 0))[0];
              return (
                <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-xs ${
                  worst?.score === 2 ? "border-red-500/40 bg-red-500/10 text-red-400"
                    : worst?.score === 1 ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]"
                    : "border-border bg-background text-muted-foreground"}`} data-testid="shipment-weather">
                  <CloudSun className="h-3.5 w-3.5" />
                  {w.origin && <span>Pickup ({w.origin.place}): <b>{w.origin.label}</b> {Math.round(w.origin.temp_hi ?? 0)}°F</span>}
                  {w.destination && <span>Delivery ({w.destination.place}): <b>{w.destination.label}</b> {Math.round(w.destination.temp_hi ?? 0)}°F</span>}
                  {worst?.score > 0 && <span className="font-bold uppercase">{worst.score === 2 ? "High delay risk" : "Watch"}</span>}
                </div>
              );
            })()}
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <Button size="sm" variant="outline" onClick={() => setSmsOpen(true)} data-testid="text-customer-btn" className="h-8 gap-1.5 text-xs">
                <MessageSquarePlus className="h-3.5 w-3.5" /> Text customer
              </Button>
              <Button size="sm" variant={repEnabled ? "default" : "outline"} onClick={() => toggleRep()} disabled={repBusy}
                data-testid="rep-sms-toggle" className="h-8 gap-1.5 text-xs">
                {repBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Smartphone className="h-3.5 w-3.5" />}
                {repEnabled ? "Texting me updates ✓" : "Text me updates"}
              </Button>
            </div>
            <div className="border-t border-border pt-3">
              <div className="label-caps mb-2 flex items-center gap-1 text-muted-foreground">
                <ClipboardList className="h-3 w-3" /> Tracking history
              </div>
              {(data.events || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No carrier scans yet — updates appear here after pickup.</p>
              ) : (
                <div className="max-h-56 space-y-2 overflow-y-auto" data-testid="shipment-status-events">
                  {data.events.map((ev, i) => (
                    <div key={i} className="flex gap-2 text-xs">
                      <div className={`mt-1 h-2 w-2 shrink-0 rounded-full ${i === 0 ? "bg-[hsl(var(--success))]" : "bg-muted-foreground/40"}`} />
                      <div>
                        <span className="font-semibold">{ev.status || "Update"}</span>
                        <span className="text-muted-foreground"> — {[[ev.city, ev.state].filter(Boolean).join(", "), ev.time].filter(Boolean).join(" · ")}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {data.checked_at && (
                <p className="mt-2 text-[11px] text-muted-foreground/70">Last updated {new Date(data.checked_at).toLocaleString()}</p>
              )}
            </div>
          </>
        )}
      </div>
      <SmsCustomerDialog quote={quote} trackingUrl={trackingUrl} open={smsOpen} onOpenChange={setSmsOpen} />
      <Dialog open={phonePromptOpen} onOpenChange={setPhonePromptOpen}>
        <DialogContent className="max-w-sm" data-testid="rep-phone-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-[hsl(var(--primary))]" /> Your mobile number</DialogTitle>
            <DialogDescription>We'll text you this shipment's updates and save the number to your profile.</DialogDescription>
          </DialogHeader>
          <Input value={phoneVal} onChange={(e) => setPhoneVal(e.target.value)} placeholder="(847) 555-1234"
            className="h-10" data-testid="rep-phone-input" autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhonePromptOpen(false)}>Cancel</Button>
            <Button onClick={() => toggleRep(phoneVal)} disabled={repBusy || !phoneVal.trim()} data-testid="rep-phone-save">
              {repBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Turn on texts"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
